import {
    getBuildInfo,
    loadBuiltInPlugins,
    loadBundledExtensions,
    loadGatewayExtensions,
    loadGatewayPlugins,
    pluginUIRegistry,
    registerReactGlobals,
    setPlatformService,
    translate,
    useLogStore,
} from "@magic-md/editor-core";
import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { createWebPlatformService } from "./platform";
import { useEngineConnectorStore } from "./platform/engine-connector";
import { resolveWebPluginLoadStrategy } from "./plugin-loading-policy";
import { resolveWebGatewayTarget } from "./startup";
import "highlight.js/styles/github.css";
import "./styles/globals.css";

const _buildInfo = getBuildInfo();

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

function envFlag(value: unknown): boolean {
    return ["1", "true", "yes", "on"].includes(String(value ?? "").toLowerCase());
}

function envNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function isLoopbackHost(): boolean {
    return window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "::1";
}

async function initWebSentry(buildInfo: typeof _buildInfo): Promise<void> {
    const sentryForced = envFlag(import.meta.env.VITE_SENTRY_ENABLED);
    const localHost = isLoopbackHost();
    const sentryEnabled = sentryForced || (import.meta.env.PROD && !localHost);
    if (!sentryEnabled) return;

    const Sentry = await import("@sentry/react");
    const tracingEnabled = envFlag(import.meta.env.VITE_SENTRY_TRACING) ||
        (import.meta.env.PROD && !localHost);
    const replayEnabled = envFlag(import.meta.env.VITE_SENTRY_REPLAY) ||
        (import.meta.env.PROD && !localHost);

    Sentry.init({
        dsn: "https://23a50dcf36148fc88c132f0c000d4de8@o1313066.ingest.us.sentry.io/4511353687703552",
        release: `magic-md-web@${buildInfo.version}${buildInfo.gitHash ? `+${buildInfo.gitHash}` : ""}`,
        environment: import.meta.env.MODE,
        sendDefaultPii: true,
        integrations: [
            ...(tracingEnabled ? [Sentry.browserTracingIntegration()] : []),
            ...(replayEnabled ? [Sentry.replayIntegration()] : []),
        ],
        tracesSampleRate: tracingEnabled
            ? envNumber(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 1.0)
            : 0,
        tracePropagationTargets: [
            "localhost",
            window.location.origin,
            /^http:\/\/127\.0\.0\.1:\d+/,
            /^http:\/\/localhost:\d+/,
        ],
        replaysSessionSampleRate: replayEnabled
            ? envNumber(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0.1)
            : 0,
        replaysOnErrorSampleRate: replayEnabled
            ? envNumber(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1.0)
            : 0,
        enableLogs: envFlag(import.meta.env.VITE_SENTRY_LOGS) || import.meta.env.PROD,
        initialScope: {
            tags: {
                app: "web",
                runtime: "browser",
                tracing: tracingEnabled ? "enabled" : "disabled",
                replay: replayEnabled ? "enabled" : "disabled",
            },
        },
    });
}

void initWebSentry(_buildInfo);

// 1. Register web platform service FIRST
setPlatformService(createWebPlatformService());

// Register React globals for external plugin UI bundles
registerReactGlobals();

// 2. Log system startup with build info
useLogStore
    .getState()
    .info(
        `Magic MD Web v${_buildInfo.version} (${_buildInfo.gitHash ?? "dev"}) built ${_buildInfo.buildTime}`,
        { source: "system" },
    );

// ── Plugin loading (engine-mode aware) ──
// DEV build  → can load from monorepo source via import.meta.glob
// PROD build → load plugin UI from the connected Gateway, same as Editor local mode

const cloudPluginSourceKinds = ["cloud-worker", "cloud-bundle"] as const;
let sourcePluginsPromise: Promise<void> | null = null;

async function loadSourcePlugins(): Promise<void> {
    if (import.meta.env.DEV) {
        if (!sourcePluginsPromise) {
            const pluginModules = {
                ...import.meta.glob("../../../plugins/*/src/ui.ts"),
                ...import.meta.glob("../../../plugins/*/src/ui.tsx"),
                ...import.meta.glob("../../../cloud-plugins/*/src/plugin/ui.ts"),
                ...import.meta.glob("../../../cloud-plugins/*/src/plugin/ui.tsx"),
            };
            sourcePluginsPromise = loadBuiltInPlugins(pluginModules);
        }
        await sourcePluginsPromise;
    }
}

let sourceExtensionsPromise: Promise<void> | null = null;

/** Load dev/source extensions from monorepo (DEV only). */
async function loadSourceExtensions(): Promise<void> {
    if (import.meta.env.DEV) {
        if (!sourceExtensionsPromise) {
            const extensionModules = {
                ...import.meta.glob("../../../extensions/*/src/ui.ts"),
                ...import.meta.glob("../../../extensions/*/src/ui.tsx"),
                ...import.meta.glob("../../../cloud-extensions/*/src/ui.ts"),
                ...import.meta.glob("../../../cloud-extensions/*/src/ui.tsx"),
            };
            sourceExtensionsPromise = loadBundledExtensions(extensionModules).then(() => undefined);
        }
        await sourceExtensionsPromise;
    }
}

/** Load extensions matching the current engine mode (mirrors plugins). */
async function loadExtensionsForMode(): Promise<void> {
    const { cloudEndpoint, localEndpoint, mode } = useEngineConnectorStore.getState();
    try {
        if (import.meta.env.DEV) await loadSourceExtensions();
        if (mode === "local") {
            await loadGatewayExtensions(localEndpoint || "http://127.0.0.1:3721");
        } else if (mode === "cloud" && cloudEndpoint) {
            await loadGatewayExtensions(cloudEndpoint);
        }
    } catch (err) {
        useLogStore.getState().warn(
            `Extension load failed: ${err instanceof Error ? err.message : String(err)}`,
            { source: "extension" },
        );
    }
}

async function loadPluginsFromGateway(
    gatewayUrl: string,
    options: {
        clearRegistry?: boolean;
        sourceKinds?: typeof cloudPluginSourceKinds[number][];
        warnIfEmpty?: boolean;
    } = {},
): Promise<void> {
    const log = useLogStore.getState();
    log.info(tr("appLog.web.loadPlugins.gateway", { url: gatewayUrl }), { source: "system" });
    if (options.clearRegistry !== false) {
        pluginUIRegistry.clear();
    }
    const result = await loadGatewayPlugins(gatewayUrl, {
        sourceKinds: options.sourceKinds,
        warnIfEmpty: options.warnIfEmpty,
    });
    if (result.loaded.length === 0 && options.warnIfEmpty !== false) {
        log.warn(tr("appLog.web.loadPlugins.noGatewayPlugins"), {
            source: "system",
        });
    }
}

async function loadPluginsForMode(): Promise<void> {
    const log = useLogStore.getState();
    const { cloudEndpoint, localEndpoint, mode } =
        useEngineConnectorStore.getState();
    const strategy = resolveWebPluginLoadStrategy({
        isDev: import.meta.env.DEV,
        mode,
    });

    if (strategy === "source") {
        log.info(tr("appLog.web.loadPlugins.devSource"), {
            source: "system",
        });
        pluginUIRegistry.clear();
        await loadSourcePlugins();
        return;
    }

    if (strategy === "source+gateway-cloud") {
        log.info(tr("appLog.web.loadPlugins.devSource"), {
            source: "system",
        });
        await loadSourcePlugins();
        await loadPluginsFromGateway(localEndpoint || "http://127.0.0.1:3721", {
            clearRegistry: false,
            sourceKinds: [...cloudPluginSourceKinds],
            warnIfEmpty: false,
        });
        return;
    }

    switch (mode) {
        case "local": {
            await loadPluginsFromGateway(localEndpoint || "http://127.0.0.1:3721");
            break;
        }
        case "cloud": {
            await loadPluginsFromGateway(cloudEndpoint);
            break;
        }
        case "none":
        default: {
            if (strategy === "none") {
                log.info(tr("appLog.engineNotConfigured"), {
                    source: "system",
                });
            }
            break;
        }
    }
}

// ?gateway=http://... — Engine Gateway 地址（make web-dev / magic-md connect 会注入）
// 无参数时默认探测本机 Gateway，避免把静态 Web 域名误判为 Gateway。
const store = useEngineConnectorStore.getState();
const gatewayTarget = resolveWebGatewayTarget(
    window.location.search,
    store.localEndpoint,
);

let prevConnected = false;
let prevMode = store.mode;
let pluginsLoading = false;

useEngineConnectorStore.subscribe((state) => {
    const { connected, mode } = state;
    const justConnected = connected && !prevConnected;
    const modeChanged = mode !== prevMode;

    if ((justConnected || (connected && modeChanged)) && !pluginsLoading) {
        pluginsLoading = true;
        useLogStore
            .getState()
            .info(
                tr("appLog.engineStateChangedReloadPlugins", { mode, connected }),
                { source: "system" },
            );
        loadPluginsForMode().finally(() => {
            pluginsLoading = false;
        });
        void loadExtensionsForMode();
    }

    if (!connected && modeChanged && mode === "none" && !import.meta.env.DEV) {
        pluginUIRegistry.clear();
    }

    prevConnected = connected;
    prevMode = mode;
});

if (store.mode === "none") {
    void loadPluginsForMode();
    void loadExtensionsForMode();
} else if (import.meta.env.DEV && store.mode === "local") {
    void loadSourcePlugins();
    void loadSourceExtensions();
}

async function connectCurrentMode(): Promise<void> {
    const success = await useEngineConnectorStore.getState().connect();
    if (!success) {
        const error = useEngineConnectorStore.getState().error;
        useLogStore
            .getState()
            .warn(tr("appLog.engine.connectFailed", { message: error ?? tr("common.unknown") }), {
                source: "engine",
            });
        if (import.meta.env.DEV) {
            void loadSourcePlugins();
        }
        return;
    }

    const connectedState = useEngineConnectorStore.getState();
    const endpoint =
        connectedState.mode === "cloud"
            ? connectedState.cloudEndpoint
            : connectedState.localEndpoint;
    useLogStore.getState().info(`Connected to Engine at ${endpoint}`, {
        source: "engine",
    });
}

async function autoConnectEngine(): Promise<void> {
    const connector = useEngineConnectorStore.getState();

    if (gatewayTarget.explicit) {
        connector.setLocalEndpoint(gatewayTarget.endpoint);
        connector.setMode("local");
        await connectCurrentMode();
        return;
    }

    if (connector.mode !== "none") {
        await connectCurrentMode();
        return;
    }

    const found = await connector.probe(gatewayTarget.endpoint);
    if (found) {
        useEngineConnectorStore.getState().setMode("local");
        await connectCurrentMode();
        return;
    }

    useLogStore.getState().info("No local Engine detected — using stub mode", {
        source: "engine",
    });
}

void autoConnectEngine();

useLogStore.getState().info("Application ready", { source: "system" });

// 5. Render
const root = document.getElementById("root");
if (root) {
    ReactDOM.createRoot(root).render(
        <React.StrictMode>
            <App />
        </React.StrictMode>,
    );
}
